import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { getData } from "../../Webservice/Webservice";

const ExpiredStock = () => {
  const [expiredStock, setExpiredStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState("completed");


  const fetchExpiredStock = async (status) => {
    try {
      setLoading(true);
      setError(null);

      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const response = await getData(`stock/expire/list?status=${status}`, null, token);

      if (response && response.expired) {
        setExpiredStock(response.expired);
      } else {
        setExpiredStock([]);
      }
    } catch (err) {
      setError("Failed to fetch expired stock data");
      console.error("Error fetching expired stock:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpiredStock(tabValue);
  }, [tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7f9", minHeight: "100vh" }}>
      {/* Header + Tabs */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h3"
          mb={2}
          component="h1"
          sx={{
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontFamily: "Mulish, sans-serif"
          }}
        >
          Expired Stock
        </Typography>

        <Box
          sx={{
            backgroundColor: "action.hover",
            borderRadius: "8px",
            p: 0.5,
            mb: 3,
            display: "inline-flex",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="expired stock status tabs"
            sx={{
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            <Tab
              label="Pending"
              value="pending"
              sx={{
                fontWeight: 700,
                textTransform: "none",
                fontFamily: "Mulish, sans-serif",
                borderRadius: "6px",
                mx: 0.5,
                py: 1,
                px: 3,
                color: tabValue === "pending" ? "black" : "text.secondary",
                backgroundColor:
                  tabValue === "pending" ? "rgb(255 209 2)" : "transparent",
                "&.Mui-selected": {
                  color: "black",
                  backgroundColor: "rgb(255 209 2)",
                },
                "&:hover": {
                  backgroundColor:
                    tabValue === "pending"
                      ? "rgb(255 209 2)"
                      : "rgb(255 209 2 / 40%)",
                },
              }}
            />
            <Tab
              label="Completed"
              value="completed"
              sx={{
                fontWeight: 700,
                textTransform: "none",
                fontFamily: "Mulish, sans-serif",
                borderRadius: "6px",
                mx: 0.5,
                py: 1,
                px: 3,
                color: tabValue === "completed" ? "black" : "text.secondary",
                backgroundColor:
                  tabValue === "completed" ? "rgb(255 209 2)" : "transparent",
                "&.Mui-selected": {
                  color: "black",
                  backgroundColor: "rgb(255 209 2)",
                },
                "&:hover": {
                  backgroundColor:
                    tabValue === "completed"
                      ? "rgb(255 209 2)"
                      : "rgb(255 209 2 / 40%)",
                },
              }}
            />
          </Tabs>
        </Box>

      </Box>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #d3d3d3de",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#f8f9fa",
                    fontWeight: 800,
                    py: 2,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#5c636a",
                    fontFamily: "Mulish, sans-serif",
                    borderBottom: "2px solid #edeff1",
                  },
                }}
              >
                <TableCell>Product Name</TableCell>
                <TableCell>UPC</TableCell>
                <TableCell>Batch No</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Manufacture Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Completed At</TableCell>
                <TableCell align="center">Quantity Expired</TableCell>
                <TableCell>Ownership</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {expiredStock.length > 0 ? (
                expiredStock.map((record) => (
                  <TableRow
                    key={record._id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#f9fafb" },
                      transition: "background-color 0.2s ease"
                    }}
                  >
                    <TableCell>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontFamily: "Mulish, sans-serif",
                          fontSize: "0.95rem",
                          color: "#1a1c1e"
                        }}
                      >
                        {record.productId?.title || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Mulish, sans-serif',
                          color: '#6b7280',
                          display: 'inline-block',
                          backgroundColor: '#f3f4f6',
                          px: 1,
                          borderRadius: '4px',
                          fontWeight: 600
                        }}
                      >
                        {record.productId?.upc || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "Mulish, sans-serif", color: "#4b5563" }}>
                        {record.stockId?.batch_no || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "Mulish, sans-serif", color: "#4b5563", fontWeight: 600 }}>
                        {record.stockId?.warehouseId?.name || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish, sans-serif", color: "#6b7280", fontSize: "0.85rem" }}>
                      {formatDate(record.stockId?.manufactureDate)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish, sans-serif", color: "#dc2626", fontWeight: 600, fontSize: "0.85rem" }}>
                      {formatDate(record.expiryDate)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish, sans-serif", color: "#6b7280", fontSize: "0.85rem" }}>
                      {formatDate(record.completedAt)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontFamily: "Mulish, sans-serif",
                          color: "#dc2626",
                          backgroundColor: "rgba(220, 38, 38, 0.1)",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          display: "inline-block"
                        }}
                      >
                        {record.quantityExpired || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={500}
                        sx={{ fontFamily: 'Mulish, sans-serif', color: '#4b5563', fontSize: '0.85rem' }}
                      >
                        {record.productId?.ownership || "N/A"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <img
                        style={{ width: '180px' }}
                        src='/img/nodata.png'
                        alt='no data'
                      />
                      <Typography variant="h6" sx={{ fontFamily: "Mulish, sans-serif", color: "text.secondary" }}>
                        No {tabValue} expired stock found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ExpiredStock;
